import { useState, useEffect } from 'react';
import { ArrowLeft, Save, Wrench, Truck as TruckIcon, DollarSign, MapPin, Search, Paperclip, Upload, X, FileText } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import { PDFParser } from './PDFParser';

interface Truck {
  id: string;
  unit_number: string;
  make: string;
  model: string;
  year: number;
}

interface Trailer {
  id: string;
  unit_number: string;
  make: string;
  model: string;
  year: number;
}

interface ParsedLineItem {
  item_type: string;
  description: string;
  quantity: number;
  rate: number;
  amount: number;
}

interface ParsedData {
  lineItems?: ParsedLineItem[];
  [key: string]: any; // Allow other properties from parsed data
}

interface CreateMaintenanceLogPageProps {
  onBack: () => void;
  onSuccess: (logId: string) => void;
}

export function CreateMaintenanceLogPage({ onBack, onSuccess }: CreateMaintenanceLogPageProps) {
  const toast = useToast();
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);
  const [trucks, setTrucks] = useState<Truck[]>([]);
  const [trailers, setTrailers] = useState<Trailer[]>([]);
  const [loadingUnits, setLoadingUnits] = useState(true);
  const [truckSearch, setTruckSearch] = useState('');
  const [trailerSearch, setTrailerSearch] = useState('');
  const [showTruckDropdown, setShowTruckDropdown] = useState(false);
  const [showTrailerDropdown, setShowTrailerDropdown] = useState(false);
  const [unitType, setUnitType] = useState<'truck' | 'trailer'>('truck');
  const [selectBothUnits, setSelectBothUnits] = useState(false);
  const [uploadedPDF, setUploadedPDF] = useState<File | null>(null);
  const [parsedData, setParsedData] = useState<ParsedData | null>(null);
  // Documents the user attaches manually (invoices, receipts, work orders).
  // Held in memory until the log is created, then uploaded in handleSubmit.
  const [attachedDocuments, setAttachedDocuments] = useState<File[]>([]);
  const [drivers, setDrivers] = useState<Array<{ id: string; first_name: string; last_name: string; email: string; phone_number?: string }>>([]);
  const [driverSearch, setDriverSearch] = useState('');
  const [showDriverDropdown, setShowDriverDropdown] = useState(false);
  const [loadingDrivers, setLoadingDrivers] = useState(false);

  const [formData, setFormData] = useState({
    maintenanceType: 'Preventive Maintenance',
    unitId: '',
    truckId: '',
    trailerId: '',
    serviceProvider: '',
    providerEmail: '',
    providerPhone: '',
    street: '',
    city: '',
    state: '',
    zipCode: '',
    serviceDate: new Date().toISOString().split('T')[0],
    odometer: '',
    description: '',
    laborCost: '',
    partsCost: '',
    cost: '',
    companyCostAmount: '',
    paidBy: 'company', // 'company', 'driver', or 'tbd'
    driverPaymentAmount: '',
    driverId: '', // ID of the driver who is paying
    glAccount: '5100-Maintenance',
    paymentType: 'Credit Card', // 'Credit Card', 'IFS', or 'ACH'
    repairLocation: 'Yard', // 'Yard' or 'Road'
  });

  const maintenanceTypes = [
    'Preventive Maintenance',
    'Oil Change',
    'Tire Service',
    'Brake Service',
    'Engine Repair',
    'Transmission Repair',
    'Electrical Repair',
    'Body Work',
    'DOT Inspection',
    'Annual Inspection',
    'Other',
  ];

  const glAccounts = [
    '5100-Maintenance',
    '5110-Repairs',
    '5120-Tires',
    '5130-Oil & Lubricants',
    '5140-Parts',
    '5150-Labor',
    '5160-Inspections',
    '5900-Other Maintenance',
  ];

  useEffect(() => {  
    loadUnits();
    loadDrivers();
  }, [unitType]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.truck-search-container')) {
        setShowTruckDropdown(false);
      }
      if (!target.closest('.trailer-search-container')) {
        setShowTrailerDropdown(false);
      }
      if (!target.closest('.driver-search-container')) {
        setShowDriverDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  async function loadDrivers() {
    setLoadingDrivers(true);
    try {
      const { data, error } = await supabase
        .from('drivers')
        .select('id, first_name, last_name, email, phone_number')
        .not('status', 'is', null)
        .order('last_name');

      if (error) throw error;
      console.log('Loaded drivers:', data);
      setDrivers(data || []);
    } catch (error) {
      console.error('Error loading drivers:', error);
      toast.error('Failed to load drivers');
    } finally {
      setLoadingDrivers(false);
    }
  }

  async function loadUnits() {
    setLoadingUnits(true);
    try {
      // Always load trucks
      const { data: trucksData, error: trucksError } = await supabase
        .from('trucks')
        .select('id, unit_number, make, model, year')
        .eq('status', 'in_service')
        .order('unit_number');

      if (trucksError) throw trucksError;
      setTrucks(trucksData || []);

      // Always load trailers
      const { data: trailersData, error: trailersError } = await supabase
        .from('trailers')
        .select('id, unit_number, make, model, year')
        .order('unit_number');

      if (trailersError) throw trailersError;
      setTrailers(trailersData || []);
    } catch (error) {
      console.error('Error loading units:', error);
      toast.error('Failed to load vehicles');
    } finally {
      setLoadingUnits(false);
    }
  }

  function handleAttachFiles(files: FileList | null) {
    if (!files || files.length === 0) return;

    const maxFileSize = 10 * 1024 * 1024; // 10MB, matching DocumentUpload
    const accepted: File[] = [];

    Array.from(files).forEach(file => {
      const isPdf = file.type === 'application/pdf';
      const isImage = file.type.startsWith('image/');
      if (!isPdf && !isImage) {
        toast.error(`${file.name}: Only PDF and image files are allowed.`);
        return;
      }
      if (file.size > maxFileSize) {
        toast.error(`${file.name}: File too large. Maximum size is 10MB.`);
        return;
      }
      accepted.push(file);
    });

    if (accepted.length > 0) {
      setAttachedDocuments(prev => [...prev, ...accepted]);
    }
  }

  function removeAttachedDocument(index: number) {
    setAttachedDocuments(prev => prev.filter((_, i) => i !== index));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (selectBothUnits) {
      if (!formData.truckId || !formData.trailerId) {
        toast.error('Please select both truck and trailer');
        return;
      }
    } else {
      const unitId = unitType === 'truck' ? formData.truckId : formData.trailerId;
      if (!unitId) {
        toast.error(`Please select a ${unitType}`);
        return;
      }
    }

    if (!formData.serviceDate) {
      toast.error('Please select a service date');
      return;
    }

    if (!formData.companyCostAmount) {
      toast.error('Please enter company cost amount');
      return;
    }

    if (!formData.glAccount) {
      toast.error('Please select a GL account');
      return;
    }

    if (formData.paidBy === 'driver' && (!formData.driverPaymentAmount || parseFloat(formData.driverPaymentAmount) <= 0)) {
      toast.error('Please enter driver payment amount');
      return;
    }

    setSaving(true);
    try {
      // Generate log number
      const { data: logNumberData, error: logNumberError } = await supabase
        .rpc('generate_maintenance_log_number');

      if (logNumberError) throw logNumberError;

      // Create maintenance log
      const { data, error } = await supabase
        .from('maintenance_logs')
        .insert({
          log_number: logNumberData,
          maintenance_type: formData.maintenanceType,
          truck_id: (unitType === 'truck' || selectBothUnits) ? formData.truckId : null,
          service_provider: formData.serviceProvider || null,
          provider_email: formData.providerEmail || null,
          provider_phone: formData.providerPhone || null,
          street: formData.street || null,
          city: formData.city || null,
          state: formData.state || null,
          zip_code: formData.zipCode || null,
          service_date: formData.serviceDate,
          odometer: formData.odometer ? parseInt(formData.odometer) : null,
          description: formData.description || null,
          cost: formData.cost ? parseFloat(formData.cost) : 0,
          company_cost_amount: parseFloat(formData.companyCostAmount),
          paid_by: formData.paidBy,
          driver_payment_amount: formData.driverPaymentAmount ? parseFloat(formData.driverPaymentAmount) : null,
          driver_id: formData.paidBy === 'driver' ? formData.driverId : null,
          gl_account: formData.glAccount,
          payment_type: formData.paymentType,
          repair_location: formData.repairLocation,
          created_by: user?.id,
        })
        .select()
        .single();

      if (error) throw error;

      if (data) {
        const { emailReminders } = await import('../../services/emailReminders');
        void emailReminders.maintenanceLogCreated({
          id: data.id, log_number: logNumberData,
          maintenance_type: formData.maintenanceType, service_date: formData.serviceDate,
          cost: formData.cost ? parseFloat(formData.cost) : 0,
        });
      }

      // Save line items if they exist
      if (parsedData?.lineItems && parsedData.lineItems.length > 0) {
        const lineItemsToInsert = parsedData.lineItems.map((item: ParsedLineItem) => ({
          maintenance_log_id: data.id,
          item_type: item.item_type,
          description: item.description,
          quantity: item.quantity,
          rate: item.rate,
          amount: item.amount
        }));

        const { error: lineItemsError } = await supabase
          .from('maintenance_line_items')
          .insert(lineItemsToInsert);

        if (lineItemsError) {
          console.error('Error saving line items:', lineItemsError);
          // Don't fail the whole operation
        } else {
          console.log(`✅ Saved ${lineItemsToInsert.length} line items`);
        }
      }

      // If a PDF was uploaded for parsing, attach it to the maintenance log
      if (uploadedPDF) {
        try {
          const timestamp = Date.now();
          const randomStr = Math.random().toString(36).substring(7);
          const fileExt = uploadedPDF.name.split('.').pop();
          const fileName = `${timestamp}-${randomStr}.${fileExt}`;
          const filePath = `${data.id}/${fileName}`;

          // Upload to storage
          const { error: uploadError } = await supabase.storage
            .from('maintenance-documents')
            .upload(filePath, uploadedPDF);

          if (uploadError) throw uploadError;

          // Save document metadata
          await supabase.from('maintenance_documents').insert({
            maintenance_log_id: data.id,
            file_name: uploadedPDF.name,
            file_type: 'pdf',
            mime_type: uploadedPDF.type,
            file_size: uploadedPDF.size,
            storage_path: filePath,
            document_type: 'Invoice',
            description: 'Auto-uploaded from PDF parser',
            uploaded_by: user?.id,
          });

          console.log('✅ PDF attached to maintenance log');
        } catch (uploadError) {
          console.error('Error uploading PDF:', uploadError);
          // Don't fail the whole operation if upload fails
        }
      }

      // Upload any documents the user attached manually.
      if (attachedDocuments.length > 0) {
        for (const file of attachedDocuments) {
          try {
            const timestamp = Date.now();
            const randomStr = Math.random().toString(36).substring(7);
            const fileExt = file.name.split('.').pop();
            const fileName = `${timestamp}-${randomStr}.${fileExt}`;
            const filePath = `${data.id}/${fileName}`;

            const { error: uploadError } = await supabase.storage
              .from('maintenance-documents')
              .upload(filePath, file);

            if (uploadError) throw uploadError;

            await supabase.from('maintenance_documents').insert({
              maintenance_log_id: data.id,
              file_name: file.name,
              file_type: file.type.startsWith('image/') ? 'image' : 'pdf',
              mime_type: file.type,
              file_size: file.size,
              storage_path: filePath,
              document_type: 'Other',
              description: 'Manually attached',
              uploaded_by: user?.id,
            });
          } catch (uploadError) {
            console.error('Error uploading attached document:', file.name, uploadError);
            // Don't fail the whole operation if one document fails
          }
        }
        console.log(`✅ Uploaded ${attachedDocuments.length} attached document(s)`);
      }

      toast.success('Maintenance log created successfully!');
      onSuccess(data.id);
    } catch (error) {
      console.error('Error creating maintenance log:', error);
      toast.error('Failed to create maintenance log');
    } finally {
      setSaving(false);
    }
  }

  function handleChange(field: string, value: string) {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      
      // Auto-calculate company cost amount based on payment split
      if (field === 'paidBy' || field === 'cost' || field === 'driverPaymentAmount') {
        const totalCost = parseFloat(newData.cost) || 0;
        const driverPayment = parseFloat(newData.driverPaymentAmount) || 0;
        
        if (newData.paidBy === 'company' || newData.paidBy === 'tbd') {
          // Company pays everything (TBD defaults to company for now)
          newData.companyCostAmount = totalCost.toString();
          newData.driverPaymentAmount = '';
        } else if (newData.paidBy === 'driver') {
          // Driver pays specified amount, company pays the rest
          newData.companyCostAmount = Math.max(0, totalCost - driverPayment).toString();
        }
      }

      // Auto-calculate total cost from labor and parts if not manually set
      if ((field === 'laborCost' || field === 'partsCost') && !newData.cost) {
        const laborCost = parseFloat(newData.laborCost) || 0;
        const partsCost = parseFloat(newData.partsCost) || 0;
        const calculatedTotal = laborCost + partsCost;
        newData.cost = calculatedTotal.toString();

        // Recalculate company cost based on new total
        const totalCost = calculatedTotal;
        const driverPayment = parseFloat(newData.driverPaymentAmount) || 0;

        if (newData.paidBy === 'company' || newData.paidBy === 'tbd') {
          newData.companyCostAmount = totalCost.toString();
        } else if (newData.paidBy === 'driver') {
          newData.companyCostAmount = Math.max(0, totalCost - driverPayment).toString();
        }
      }
      
      return newData;
    });
  }

  async function handleParsedData(data: ParsedData) {
    console.log('📝 Applying parsed data to form:', data);
    
    // Store the full parsed data including line items
    setParsedData(data);
    
    let vehicleMatched = false;
    
    // Try to match by Unit Number first (more reliable)
    if (data.unitNumber && !vehicleMatched) {
      console.log('🔍 Searching for vehicle with Unit #:', data.unitNumber);
      
      // Search trucks
      const { data: truckData } = await supabase
        .from('trucks')
        .select('id, unit_number, vin')
        .eq('unit_number', data.unitNumber)
        .single();
      
      if (truckData) {
        console.log('✅ Found matching truck by unit number:', truckData.unit_number);
        data.truckId = truckData.id;
        setUnitType('truck');
        setTruckSearch(truckData.unit_number);
        toast.success(`Auto-matched truck: ${truckData.unit_number}`);
        vehicleMatched = true;
      } else {
        // Search trailers
        const { data: trailerData } = await supabase
          .from('trailers')
          .select('id, unit_number, vin')
          .eq('unit_number', data.unitNumber)
          .single();
        
        if (trailerData) {
          console.log('✅ Found matching trailer by unit number:', trailerData.unit_number);
          data.trailerId = trailerData.id;
          setUnitType('trailer');
          setTrailerSearch(trailerData.unit_number);
          toast.success(`Auto-matched trailer: ${trailerData.unit_number}`);
          vehicleMatched = true;
        }
      }
    }
    
    // If not matched by unit number, try VIN
    if (data.vin && !vehicleMatched) {
      console.log('🔍 Searching for vehicle with VIN:', data.vin);
      
      // Search trucks
      const { data: truckData } = await supabase
        .from('trucks')
        .select('id, unit_number, vin')
        .eq('vin', data.vin)
        .single();
      
      if (truckData) {
        console.log('✅ Found matching truck by VIN:', truckData.unit_number);
        data.truckId = truckData.id;
        setUnitType('truck');
        setTruckSearch(truckData.unit_number);
        toast.success(`Auto-matched truck: ${truckData.unit_number}`);
        vehicleMatched = true;
      } else {
        // Search trailers
        const { data: trailerData } = await supabase
          .from('trailers')
          .select('id, unit_number, vin')
          .eq('vin', data.vin)
          .single();
        
        if (trailerData) {
          console.log('✅ Found matching trailer by VIN:', trailerData.unit_number);
          data.trailerId = trailerData.id;
          setUnitType('trailer');
          setTrailerSearch(trailerData.unit_number);
          toast.success(`Auto-matched trailer: ${trailerData.unit_number}`);
          vehicleMatched = true;
        }
      }
    }
    
    if (!vehicleMatched && (data.unitNumber || data.vin)) {
      console.log('⚠️ No vehicle found');
      toast.warning('Vehicle info found but no match in system');
    }
    
    // Merge parsed data with existing form data
    const filteredData = Object.fromEntries(
      Object.entries(data).filter(([_, v]) => v !== undefined && v !== '')
    );
    
    console.log('📝 Filtered data to apply:', filteredData);
    
    setFormData(prev => {
      const newData = {
        ...prev,
        ...filteredData,
      };
      console.log('📝 New form data:', newData);
      return newData;
    });
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-0 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={onBack}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-slate-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Create Maintenance Log</h1>
                <p className="text-sm text-slate-600">Record maintenance activity for a vehicle</p>
              </div>
            </div>
            <button
              onClick={handleSubmit}
              disabled={saving}
              className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-5 h-5" />
              {saving ? 'Saving...' : 'Save Log'}
            </button>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-5xl mx-auto px-6 py-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* PDF Parser */}
          <PDFParser
            onDataParsed={handleParsedData}
            onFileSelected={setUploadedPDF}
          />

          {/* Maintenance Type & Truck */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-blue-100 p-3 rounded-lg">
                <Wrench className="w-6 h-6 text-blue-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-900">Service Information</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Maintenance Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.maintenanceType}
                  onChange={(e) => handleChange('maintenanceType', e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  {maintenanceTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div className="relative unit-search-container">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Unit <span className="text-red-500">*</span>
                </label>
                
                {/* Unit Type Toggle */}
                <div className="flex gap-2 mb-2">
                  <button
                    type="button"
                    onClick={() => {
                      if (!selectBothUnits) {
                        setUnitType('truck');
                        setTruckSearch('');
                        handleChange('trailerId', '');
                      }
                    }}
                    className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
                      unitType === 'truck' || selectBothUnits
                        ? 'bg-blue-600 text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                    disabled={selectBothUnits}
                  >
                    Truck
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (!selectBothUnits) {
                        setUnitType('trailer');
                        setTrailerSearch('');
                        handleChange('truckId', '');
                      }
                    }}
                    className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
                      unitType === 'trailer' || selectBothUnits
                        ? 'bg-blue-600 text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                    disabled={selectBothUnits}
                  >
                    Trailer
                  </button>
                </div>

                {/* Checkbox to select both */}
                <div className="flex items-center gap-2 mb-3">
                  <input
                    type="checkbox"
                    id="selectBothUnits"
                    checked={selectBothUnits}
                    onChange={(e) => {
                      setSelectBothUnits(e.target.checked);
                      if (e.target.checked) {
                        setUnitType('truck');
                      } else {
                        handleChange('truckId', '');
                        handleChange('trailerId', '');
                        setTruckSearch('');
                        setTrailerSearch('');
                      }
                    }}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="selectBothUnits" className="text-sm font-medium text-slate-700 cursor-pointer">
                    Service both Truck and Trailer
                  </label>
                </div>

                {/* Truck Selector */}
                {(unitType === 'truck' || selectBothUnits) && (
                  <div className="relative mb-3 truck-search-container">
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {selectBothUnits ? 'Select Truck' : 'Search Truck'}
                    </label>
                    <Search className="absolute left-3 bottom-2.5 w-5 h-5 text-slate-400" />
                    <input
                      type="text"
                      value={truckSearch || (formData.truckId ? trucks.find(t => t.id === formData.truckId)?.unit_number || '' : '')}
                      onChange={(e) => {
                        setTruckSearch(e.target.value);
                        setShowTruckDropdown(true);
                        if (!e.target.value) {
                          handleChange('truckId', '');
                        }
                      }}
                      onFocus={() => {
                        setShowTruckDropdown(true);
                      }}
                      className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Search trucks by unit number..."
                      disabled={loadingUnits}
                    />
                    {showTruckDropdown && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-slate-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {trucks
                          .filter(truck => 
                            truck.unit_number.toLowerCase().includes(truckSearch.toLowerCase()) ||
                            `${truck.make} ${truck.model}`.toLowerCase().includes(truckSearch.toLowerCase())
                          )
                          .map(truck => (
                            <button
                              key={truck.id}
                              type="button"
                              onClick={() => {
                                handleChange('truckId', truck.id);
                                setTruckSearch(truck.unit_number);
                                setShowTruckDropdown(false);
                              }}
                              className="w-full px-4 py-2 text-left hover:bg-blue-50 transition-colors border-b border-slate-100 last:border-0"
                            >
                              <div className="font-semibold text-slate-900">{truck.unit_number}</div>
                              <div className="text-sm text-slate-600">{truck.year} {truck.make} {truck.model}</div>
                            </button>
                          ))}
                        {trucks.filter(truck => 
                          truck.unit_number.toLowerCase().includes(truckSearch.toLowerCase()) ||
                          `${truck.make} ${truck.model}`.toLowerCase().includes(truckSearch.toLowerCase())
                        ).length === 0 && (
                          <div className="px-4 py-3 text-sm text-slate-500 text-center">
                            No trucks found
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Trailer Selector */}
                {(unitType === 'trailer' || selectBothUnits) && (
                  <div className="relative trailer-search-container">
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {selectBothUnits ? 'Select Trailer' : 'Search Trailer'}
                    </label>
                    <Search className="absolute left-3 bottom-2.5 w-5 h-5 text-slate-400" />
                    <input
                      type="text"
                      value={trailerSearch || (formData.trailerId ? trailers.find(t => t.id === formData.trailerId)?.unit_number || '' : '')}
                      onChange={(e) => {
                        setTrailerSearch(e.target.value);
                        setShowTrailerDropdown(true);
                        if (!e.target.value) {
                          handleChange('trailerId', '');
                        }
                      }}
                      onFocus={() => {
                        setShowTrailerDropdown(true);
                      }}
                      className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Search trailers by unit number..."
                      disabled={loadingUnits}
                    />
                    {showTrailerDropdown && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-slate-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {trailers
                          .filter(trailer => 
                            trailer.unit_number.toLowerCase().includes(trailerSearch.toLowerCase()) ||
                            `${trailer.make} ${trailer.model}`.toLowerCase().includes(trailerSearch.toLowerCase())
                          )
                          .map(trailer => (
                            <button
                              key={trailer.id}
                              type="button"
                              onClick={() => {
                                handleChange('trailerId', trailer.id);
                                setTrailerSearch(trailer.unit_number);
                                setShowTrailerDropdown(false);
                              }}
                              className="w-full px-4 py-2 text-left hover:bg-blue-50 transition-colors border-b border-slate-100 last:border-0"
                            >
                              <div className="font-semibold text-slate-900">{trailer.unit_number}</div>
                              <div className="text-sm text-slate-600">{trailer.year} {trailer.make} {trailer.model}</div>
                            </button>
                          ))}
                        {trailers.filter(trailer => 
                          trailer.unit_number.toLowerCase().includes(trailerSearch.toLowerCase()) ||
                          `${trailer.make} ${trailer.model}`.toLowerCase().includes(trailerSearch.toLowerCase())
                        ).length === 0 && (
                          <div className="px-4 py-3 text-sm text-slate-500 text-center">
                            No trailers found
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Service Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={formData.serviceDate}
                  onChange={(e) => handleChange('serviceDate', e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Odometer
                </label>
                <input
                  type="number"
                  value={formData.odometer}
                  onChange={(e) => handleChange('odometer', e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Current mileage"
                  min="0"
                />
              </div>
            </div>
          </div>

          {/* Service Provider */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-green-100 p-3 rounded-lg">
                <TruckIcon className="w-6 h-6 text-green-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-900">Service Provider</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Provider Name
                </label>
                <input
                  type="text"
                  value={formData.serviceProvider}
                  onChange={(e) => handleChange('serviceProvider', e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="ABC Truck Repair"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Provider Email
                </label>
                <input
                  type="email"
                  value={formData.providerEmail}
                  onChange={(e) => handleChange('providerEmail', e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="service@provider.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Provider Phone
                </label>
                <input
                  type="tel"
                  value={formData.providerPhone}
                  onChange={(e) => handleChange('providerPhone', e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>
          </div>

          {/* Service Location */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-purple-100 p-3 rounded-lg">
                <MapPin className="w-6 h-6 text-purple-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-900">Service Location</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Street Address
                </label>
                <input
                  type="text"
                  value={formData.street}
                  onChange={(e) => handleChange('street', e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="123 Service Road"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  City
                </label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => handleChange('city', e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Chicago"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  State
                </label>
                <input
                  type="text"
                  value={formData.state}
                  onChange={(e) => handleChange('state', e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="IL"
                  maxLength={2}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Zip Code
                </label>
                <input
                  type="text"
                  value={formData.zipCode}
                  onChange={(e) => handleChange('zipCode', e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="60601"
                />
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Description
              <span className="text-slate-500 ml-2 text-xs">Max length: 2000 characters</span>
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              rows={4}
              maxLength={2000}
              placeholder="Describe the maintenance work performed..."
            />
            <div className="text-right text-xs text-slate-500 mt-1">
              {formData.description.length} / 2000 characters
            </div>
          </div>

          {/* Documents / Attachments */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-purple-100 p-3 rounded-lg">
                <Paperclip className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-900">Documents</h2>
                <p className="text-sm text-slate-500">Attach invoices, receipts, or work orders (PDF or image, max 10MB)</p>
              </div>
            </div>

            <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-300 rounded-lg p-6 cursor-pointer hover:border-purple-400 hover:bg-purple-50/40 transition-colors">
              <Upload className="w-8 h-8 text-slate-400" />
              <span className="text-sm font-medium text-slate-700">Click to upload document(s)</span>
              <span className="text-xs text-slate-500">PDF, JPG, or PNG</span>
              <input
                type="file"
                multiple
                accept="application/pdf,image/*"
                className="hidden"
                onChange={(e) => {
                  handleAttachFiles(e.target.files);
                  e.target.value = '';
                }}
              />
            </label>

            {attachedDocuments.length > 0 && (
              <ul className="mt-4 space-y-2">
                {attachedDocuments.map((file, index) => (
                  <li
                    key={`${file.name}-${index}`}
                    className="flex items-center justify-between gap-3 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <FileText className="w-5 h-5 text-purple-600 flex-shrink-0" />
                      <span className="text-sm text-slate-700 truncate">{file.name}</span>
                      <span className="text-xs text-slate-400 flex-shrink-0">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeAttachedDocument(index)}
                      className="text-slate-400 hover:text-red-600 transition-colors flex-shrink-0"
                      title="Remove"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Financial Information */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-orange-100 p-3 rounded-lg">
                <DollarSign className="w-6 h-6 text-orange-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-900">Financial Information</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Labor Cost
                </label>
                <input
                  type="number"
                  value={formData.laborCost}
                  onChange={(e) => handleChange('laborCost', e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Parts Cost
                </label>
                <input
                  type="number"
                  value={formData.partsCost}
                  onChange={(e) => handleChange('partsCost', e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Total Cost
                </label>
                <input
                  type="number"
                  value={formData.cost}
                  onChange={(e) => handleChange('cost', e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  GL Account <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.glAccount}
                  onChange={(e) => handleChange('glAccount', e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  {glAccounts.map(account => (
                    <option key={account} value={account}>{account}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Payment Information */}
            <div className="mt-8 pt-6 border-t border-slate-200">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Payment Information</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Who is paying?
                  </label>
                  <select
                    value={formData.paidBy}
                    onChange={(e) => handleChange('paidBy', e.target.value)}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="company">Company</option>
                    <option value="driver">Driver</option>
                    <option value="tbd">TBD (To Be Decided)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Driver Payment Amount {formData.paidBy === 'driver' && <span className="text-red-500">*</span>}
                  </label>
                  <input
                    type="number"
                    value={formData.driverPaymentAmount}
                    onChange={(e) => handleChange('driverPaymentAmount', e.target.value)}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    disabled={formData.paidBy === 'company' || formData.paidBy === 'tbd'}
                  />
                  {formData.paidBy === 'company' && (
                    <p className="text-xs text-slate-500 mt-1">Company pays full amount</p>
                  )}
                  {formData.paidBy === 'tbd' && (
                    <p className="text-xs text-amber-500 mt-1">Payment responsibility to be decided</p>
                  )}
                </div>
              </div>

              {/* Driver Selection - Only show when driver is paying */}
              {formData.paidBy === 'driver' && (
                <div className="mt-6">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Select Driver <span className="text-red-500">*</span>
                  </label>
                  <div className="relative driver-search-container">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="text"
                      value={driverSearch || (formData.driverId ? drivers.find(d => d.id === formData.driverId)?.first_name + ' ' + drivers.find(d => d.id === formData.driverId)?.last_name || '' : '')}
                      onChange={(e) => {
                        setDriverSearch(e.target.value);
                        setShowDriverDropdown(true);
                        if (!e.target.value) {
                          handleChange('driverId', '');
                        }
                      }}
                      onFocus={() => setShowDriverDropdown(true)}
                      className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Search drivers by name..."
                      disabled={loadingDrivers}
                    />
                    {showDriverDropdown && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-slate-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {drivers
                          .filter(driver => 
                            driver.first_name.toLowerCase().includes(driverSearch.toLowerCase()) ||
                            driver.last_name.toLowerCase().includes(driverSearch.toLowerCase()) ||
                            `${driver.first_name} ${driver.last_name}`.toLowerCase().includes(driverSearch.toLowerCase())
                          )
                          .map(driver => (
                            <button
                              key={driver.id}
                              type="button"
                              onClick={() => {
                                handleChange('driverId', driver.id);
                                setDriverSearch(`${driver.first_name} ${driver.last_name}`);
                                setShowDriverDropdown(false);
                              }}
                              className="w-full px-4 py-2 text-left hover:bg-blue-50 transition-colors border-b border-slate-100 last:border-0"
                            >
                              <div className="font-semibold text-slate-900">{driver.first_name} {driver.last_name}</div>
                              <div className="text-sm text-slate-600">
                                {driver.email || driver.phone_number || 'No contact info'}
                              </div>
                            </button>
                          ))}
                        {drivers.filter(driver => 
                          driver.first_name.toLowerCase().includes(driverSearch.toLowerCase()) ||
                          driver.last_name.toLowerCase().includes(driverSearch.toLowerCase()) ||
                          `${driver.first_name} ${driver.last_name}`.toLowerCase().includes(driverSearch.toLowerCase())
                        ).length === 0 && (
                          <div className="px-4 py-3 text-sm text-slate-500 text-center">
                            No drivers found
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Payment Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.paymentType}
                    onChange={(e) => handleChange('paymentType', e.target.value)}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="Credit Card">Credit Card</option>
                    <option value="IFS">IFS</option>
                    <option value="ACH">ACH</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Repair Location <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.repairLocation}
                    onChange={(e) => handleChange('repairLocation', e.target.value)}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="Yard">Yard</option>
                    <option value="Road">Road</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Company Cost Amount <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={formData.companyCostAmount}
                    onChange={(e) => handleChange('companyCostAmount', e.target.value)}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-slate-50"
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    readOnly
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    {formData.paidBy === 'company'
                      ? 'Company pays full amount'
                      : formData.paidBy === 'tbd'
                        ? 'Payment responsibility to be decided'
                        : `Total: $${parseFloat(formData.cost || '0').toFixed(2)} - Driver: $${parseFloat(formData.driverPaymentAmount || '0').toFixed(2)}`
                    }
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    GL Account <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.glAccount}
                    onChange={(e) => handleChange('glAccount', e.target.value)}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    {glAccounts.map(account => (
                      <option key={account} value={account}>{account}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
